import React, { useEffect, useRef, useState } from 'react'

import Carousel from 'react-multi-carousel'

import {
  useActions,
  useQuery,
  useTypedSelector,
  useWindowSize,
} from 'shared/hooks'
import { scrollIntoElement } from 'shared/utils'
import { detectDevice } from 'shared/utils/detectDevice'
import { useNavigate } from 'react-router-dom'

import { GitlabServicesMtsRuMtsMediaCorporateCorporateBackendProjectsProject as Projects } from 'api/models'

import 'react-multi-carousel/lib/styles.css'
import classes from './Slider.module.scss'
import carouselClasses from 'styles/carousel.module.scss'
import { ButtonGroup } from 'shared/components'

const sliderSettings = (showDots: boolean) => ({
  className: classes.slider,
  arrows: false,
  centerMode: true,
  draggable: true,
  focusOnSelect: false,
  infinite: true,
  itemClass: classes.slider_item,
  keyBoardControl: false,
  minimumTouchDrag: 80,
  renderDotsOutside: true,
  showDots: showDots,
  dotListClass: carouselClasses.dotList,
  renderButtonGroupOutside: true,
  customButtonGroup: !showDots ? <ButtonGroup noMagnet={true} /> : null,
  swipeable: true,
  responsive: {
    desktop: {
      breakpoint: { max: 3000, min: 719 },
      items: 3,
    },
    mobile: {
      breakpoint: { max: 719, min: 0 },
      items: 1,
    },
  },
})

export const Slider = (): JSX.Element => {
  const [query] = useQuery()
  const { isDesktop, isLargeDesktop, isMobile } = useWindowSize()
  const [sliderConfig, setSliderConfig] = useState(sliderSettings(!isDesktop))
  const navigate = useNavigate()
  const { catalog } = useTypedSelector(state => state.catalog)
  const [swappedCatalog, setSwappedCatalog] = useState<Projects[]>([])
  const { fetchProjects } = useActions()
  const isTablet = detectDevice()
  const [transformPlaceHolder, setTransformPlaceHolder] = useState<
    undefined | number
  >(undefined)
  const ref = useRef<Carousel>(null)
  const isDraft = query?.draftListId

  useEffect(() => {
    fetchProjects(false, {
      size: 0,
      offset: 0,
      displayOnHomePage: true,
      draftListId: isDraft,
    })

    if (isDraft) {
      scrollIntoElement('originals-main-slider', undefined, undefined, true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (isDraft && catalog.length > 1) {
      const temp = [...catalog]
      temp.splice(0, 2)
      setSwappedCatalog([catalog[1], catalog[0], ...temp])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalog])

  useEffect(() => {
    setSliderConfig(sliderSettings(!isDesktop && !isLargeDesktop))
  }, [isDesktop, isLargeDesktop])

  const handleClick = (
    ev: React.MouseEvent<HTMLDivElement, MouseEvent>,
    id: string
  ) => {
    if (transformPlaceHolder !== ref?.current?.state?.transform) {
      ev.preventDefault()
      return
    } else {
      const link =
        isDraft === id ? `/catalog?draftOpen=${id}` : `/catalog?id=${id}`
      navigate(link, { replace: false })
    }
  }

  const renderCarousel = () => {
    const _catalog = isDraft ? swappedCatalog : catalog

    return (
      <Carousel
        ref={ref}
        autoPlay={!isDraft && !isTablet && !isMobile}
        {...sliderConfig}
      >
        {_catalog.map((item, itemIndex) => (
          <div
            key={itemIndex}
            className={classes.slider_item_bg}
            style={{
              backgroundImage: `url(${item?.imageSquare?.image?.href})`,
            }}
            onClick={ev => handleClick(ev, item.uid)}
          ></div>
        ))}
      </Carousel>
    )
  }

  return (
    <div
      id="originals-main-slider"
      data-reveal-delay={30}
      className={`${classes.slider_wrap} reveal-from-bottom-medium`}
      onMouseDown={() => {
        setTransformPlaceHolder(ref?.current?.state?.transform)
      }}
      onMouseUp={() => {
        setTransformPlaceHolder(ref?.current?.state?.transform)
      }}
      onTouchStart={() =>
        setTransformPlaceHolder(ref?.current?.state?.transform)
      }
      onTouchEnd={() => setTransformPlaceHolder(ref?.current?.state?.transform)}
    >
      {renderCarousel()}
    </div>
  )
}
